import { htmlDecode, capitalize, sameJson } from "@/utils/general"
import pageNumberTransformation from "@/modules/flatplan/pageNumberTransformation"
import { noneId } from "@/modules/category/store"
import { fragmentText } from "@/modules/page/useFragment"

// describe() is the only place that turns activity rows into words and page numbers; data stores raw
// old -> new values.

const SHORT = 150

// A sentence is an array of parts: plain text (a string), or an object whose kind says what it is:
// - page: a page reference { text, pageId }, linked where the list can route to pages
// - more: a shortened text { text, full } that "show more" expands
// - swatch: a category's colour { color }; dot: a tag's colour { color }, round as under a page
// - drawing: a page drawn before and after { pageDiff: { before, after, labels }, key } (either side null), keyed by its page

const quote = (value) => {
  value = htmlDecode(value || "")
  return value.length > SHORT ? { kind: "more", text: `"${value.slice(0, SHORT)}…"`, full: `"${value}"` } : `"${value}"`
}

// 'Subheading: "Old" → "New"'
const changed = (label, before, after) => [label, quote(before), " → ", quote(after)]

const plural = (n, noun) => (n === 1 ? noun : `${noun}s`)

// "a page", "3 pages"
const quantity = (n, noun) => `${n === 1 ? "a" : n} ${plural(n, noun)}`

// What each op did
const DONE = { create: "added", restore: "restored", update: "updated", delete: "deleted" }

// Every page index a row refers to: slots (indexes in the flatplan order), and the indexes
// stitch-ins and page numbering restarts store
const indexesOf = (activity) => [
  ...activity.changes.flatMap(({ slot, before, after }) => [slot, ...[before, after].flatMap((values) => [values?.pageNumber, ...(values?.pageNumberings || []).map((restart) => +restart.pagePos)])]),
  ...(activity.moves || []).flatMap((move) => [move.from.slot, move.to.slot]),
]

// The page numbers a row's indexes read as, with today's numbering settings.
// The count is raised to reach the row's highest index, so pages deleted from the end still get numbers.
const pageNumbersFor = (activity, { numbering }) => {
  let indexes = indexesOf(activity).filter((index) => index != null && !isNaN(index))
  return pageNumberTransformation({ ...numbering, noOfPages: Math.max(numbering.noOfPages, ...indexes.map((index) => index + 1)) })
}

// "p. 12, 13"
const pageList = (slots, ctx) => `p. ${slots.map((slot) => ctx.pageNumbers[slot]).join(", ")}`

// "p. 4–5" when the slots follow on from each other
const pageSpan = (slots, ctx) => {
  let followOn = slots.length > 1 && slots.every((slot, i) => i === 0 || slot === slots[i - 1] + 1)
  return followOn ? `p. ${ctx.pageNumbers[slots[0]]}–${ctx.pageNumbers[slots.at(-1)]}` : pageList(slots, ctx)
}

const pageRef = (pageId, ctx) => {
  if (!ctx.pageIds.has(pageId)) return "a deleted page"
  let slot = ctx.order.indexOf(pageId)
  return { kind: "page", text: slot === -1 ? "a page in the holding area" : `p. ${ctx.pageNumbers[slot]}`, pageId }
}

// A fragment's text with its linked content's as frozen in the row's labels
export const frozenText = (fragment, labels) => fragmentText(fragment, (contentId) => labels[contentId]?.text)

// the page dialog stores its <none> category as an id
const categoryOf = (fragment) => (fragment.categoryId === noneId ? null : fragment.categoryId || null)

const categoryName = (categoryId, labels) => (categoryId ? labels[categoryId]?.name || "a deleted category" : "none")

const typeOf = (fragment) => fragment.type || "editorial"

const fragmentSentences = (before, after, labels) => {
  let sentences = []
  let count = Math.max(before.length, after.length)

  for (let i = 0; i < count; i++) {
    let [b, a] = [before[i], after[i]]
    let slot = `Fragment ${i + 1}`

    if (!b || !a) {
      // a blank fragment added or removed is only the template's doing, which the drawing shows
      let text = frozenText(a || b, labels)
      if (text) sentences.push([`${slot} ${a ? "added" : "removed"}: `, quote(text)])
    } else {
      let [bText, aText] = [frozenText(b, labels) || "", frozenText(a, labels) || ""]
      if (bText !== aText) sentences.push(changed(`${slot}: `, bText, aText))
      if (categoryOf(b) !== categoryOf(a)) sentences.push([`${slot} category: ${categoryName(categoryOf(b), labels)} → ${categoryName(categoryOf(a), labels)}`])
      if (typeOf(b) !== typeOf(a)) sentences.push([`${slot}: ${typeOf(b)} → ${typeOf(a)}`])
    }
  }

  return sentences
}

// Tags without a label had already been deleted when the edit was made: dropping them is no news.
const tagSentences = (before, after, labels) => {
  let sentence = (verb, ids) => [`${verb} ${plural(ids.length, "tag")} ${ids.map((id) => labels[id].name).join(", ")}`]
  let added = after.filter((id) => labels[id] && !before.includes(id))
  let removed = before.filter((id) => labels[id] && !after.includes(id))
  let sentences = []

  if (added.length) sentences.push(sentence("Added", added))
  if (removed.length) sentences.push(sentence("Removed", removed))
  return sentences
}

const imageSentence = (before, after) => {
  let [b, a] = [before?.key, after?.key]
  if (!b && a) return [["Added the page image"]]
  if (b && !a) return [["Removed the page image"]]
  if (b !== a || before.page !== after.page) return [["Replaced the page image"]] // a PDF's pages share its key
  return []
}

const notesSentence = (before, after, what = "notes") => {
  if (!before) return [[`Added ${what}: `, quote(after)]]
  if (!after) return [[`Removed ${what}: `, quote(before)]]
  return [changed(`${capitalize(what)}: `, before, after)]
}

const drawing = (change, labels) => ({ kind: "drawing", pageDiff: { before: change.before || null, after: change.after || null, labels }, key: change._id })

// pages drawn side by side as one sentence
const drawings = (changes, labels) => (changes.length ? [changes.map((change) => drawing(change, labels))] : [])

// An update's sentences. Each word covers some fields and reads when any of them is in the update, in
// the order the words are listed. A field no word covers, which data records before the app words it,
// reads "Changed <field>".
const fieldSentences = (words, before, after, extra) => {
  let fields = new Set([...Object.keys(before), ...Object.keys(after)])
  let covered = new Set(words.flatMap(([covers]) => covers))
  return [
    ...words.filter(([covers]) => covers.some((field) => fields.has(field))).flatMap(([, word]) => word(before, after, extra)),
    ...[...fields].filter((field) => !covered.has(field) && !sameJson(before[field], after[field])).map((field) => [`Changed ${field}`]),
  ].filter(Boolean)
}

// data stores template, fragments and tags with every edit: the drawing shows them all
const PAGE_WORDS = [
  [["template", "fragments"], (before, after, { change, labels }) => [[drawing(change, labels)], ...fragmentSentences(before.fragments || [], after.fragments || [], labels)]],
  [["tagRefs"], (before, after, { labels }) => tagSentences(before.tagRefs || [], after.tagRefs || [], labels)],
  [["thumbnail"], (before, after) => imageSentence(before.thumbnail, after.thumbnail)],
  [["isLocked"], (before, after) => [[after.isLocked ? "Locked the page" : "Unlocked the page"]]],
  [["notes"], (before, after) => notesSentence(before.notes, after.notes)],
]

// What an edit changed, without saying which page
const editSentences = (change, labels) => (change.op === "update" ? fieldSentences(PAGE_WORDS, change.before, change.after, { change, labels }) : [])

const pageEdit = (change, labels, ctx) => {
  let sentences = editSentences(change, labels)
  return sentences.length ? [["Edited ", pageRef(change._id, ctx)], ...sentences] : []
}

// "p. 5–8" or "p. 5, 7"; a count when some are no longer on the flatplan
const pageIdsSpan = (pageIds, ctx) => {
  let slots = pageIds.map((pageId) => ctx.order.indexOf(pageId)).sort((a, b) => a - b)
  return slots.includes(-1) ? quantity(pageIds.length, "page") : pageSpan(slots, ctx)
}

// Batch writes store their changes in any order
const inPageOrder = (changes, ctx) => [...changes].sort((a, b) => ctx.order.indexOf(a._id) - ctx.order.indexOf(b._id))

// Stretch and paste: one entry for the copy, then what each page it was copied onto lost and gained.
// A paste's one target is linked in the headline; a stretch's targets each head their own changes.
const copyRow = (activity, ctx) => {
  let targets = inPageOrder(activity.changes, ctx)
    .map((change) => ({ pageId: change._id, sentences: editSentences(change, activity.labels || {}) }))
    .filter((target) => target.sentences.length)
  if (targets.length === 0) return []

  let copied = ["Copied ", pageRef(activity.copiedFrom, ctx), " onto "]
  if (targets.length === 1) return [[...copied, pageRef(targets[0].pageId, ctx)], ...targets[0].sentences]
  let pageIds = targets.map((target) => target.pageId)
  return [[...copied, pageIdsSpan(pageIds, ctx)], ...targets.flatMap((target) => [["On ", pageRef(target.pageId, ctx), ":"], ...target.sentences])]
}

// Moves only hold the pages the user moved: data leaves out the ones that shifted.
// Each sentence is followed by its pages as they looked, which rows recorded before data stored that don't have.
const moveSentences = (moves, labels, ctx) => {
  let byNewSlot = (a, b) => a.to.slot - b.to.slot
  let onFlatplan = moves.filter((move) => !move.from.holding && !move.to.holding).sort(byNewSlot)
  let toHolding = moves.filter((move) => !move.from.holding && move.to.holding).sort((a, b) => a.from.slot - b.from.slot)
  let fromHolding = moves.filter((move) => move.from.holding && !move.to.holding).sort(byNewSlot)
  let from = (list) => list.map((move) => move.from.slot)
  let to = (list) => list.map((move) => move.to.slot)
  let drawn = (list) =>
    drawings(
      list.filter((move) => move.page?.fragments).map((move) => ({ _id: move._id, after: move.page })),
      labels
    )
  let sentences = []

  if (onFlatplan.length) sentences.push([`Moved ${pageList(from(onFlatplan), ctx)} → ${pageSpan(to(onFlatplan), ctx)}`], ...drawn(onFlatplan))
  if (toHolding.length) sentences.push([`Moved ${pageList(from(toHolding), ctx)} to the holding area`], ...drawn(toHolding))
  if (fromHolding.length) sentences.push([`Moved ${quantity(fromHolding.length, "page")} from the holding area to ${pageSpan(to(fromHolding), ctx)}`], ...drawn(fromHolding))
  return sentences
}

// Pages and spaces added, restored or deleted: those on the flatplan by slot, those on the holding area (a
// null slot) by count. Added pages read by the page they went in after, which is drawn under them.
const orderChangeSentences = (changes, ctx) => {
  let isSpace = (change) => !!(change.after || change.before)?.isSpace
  let slots = (list) => list.map((change) => change.slot).sort((a, b) => a - b)
  let added = (placed) => `${quantity(placed.length, "page")} ${placed[0] > 0 ? `after p. ${ctx.pageNumbers[placed[0] - 1]}` : `at ${pageList(placed.slice(0, 1), ctx)}`}`

  return ["create", "restore", "delete"].flatMap((op) => {
    let verb = capitalize(DONE[op])
    let ofOp = changes.filter((change) => change.op === op)
    let spaces = slots(ofOp.filter(isSpace))
    let pages = ofOp.filter((change) => !isSpace(change))
    let placed = slots(pages.filter((change) => change.slot != null))
    let held = pages.length - placed.length

    return [
      placed.length && `${verb} ${op === "create" ? added(placed) : pageList(placed, ctx)}`,
      held && `${verb} ${quantity(held, "page")} ${op === "delete" ? "from" : "to"} the holding area`,
      spaces.length && (op === "delete" ? `Removed ${quantity(spaces.length, "space")}` : `${verb} ${quantity(spaces.length, "space")} at ${pageList(spaces, ctx)}`),
    ]
      .filter(Boolean)
      .map((sentence) => [sentence])
  })
}

// Deleted pages drawn as they were, side by side in page order, the holding area's last
const deletedDrawings = (changes, labels) =>
  drawings(
    changes.filter((change) => change.op === "delete" && change.before?.fragments).sort((a, b) => (a.slot ?? Infinity) - (b.slot ?? Infinity)),
    labels
  )

// the page added pages went in after, as it looked; rows recorded before data stored it don't have it
const insertedAfterDrawing = (insertedAfter, labels) => drawings(insertedAfter?.page?.fragments ? [{ _id: insertedAfter._id, after: insertedAfter.page }] : [], labels)

const pageRow = (activity, ctx) => {
  if (activity.copiedFrom) return copyRow(activity, ctx)

  let labels = activity.labels || {}
  return [
    ...moveSentences(activity.moves || [], labels, ctx),
    ...orderChangeSentences(activity.changes, ctx),
    ...insertedAfterDrawing(activity.insertedAfter, labels),
    ...deletedDrawings(activity.changes, labels),
    ...inPageOrder(activity.changes, ctx).flatMap((change) => pageEdit(change, labels, ctx)),
  ]
}

// Restarts by the number their page had before the change, removed ones by the number it has after
const restartSentences = (before, after, ctx) => {
  let numbers = (values) => pageNumberTransformation({ ...ctx.numbering, noOfPages: ctx.pageNumbers.length, ...values, pageNumberings: values.pageNumberings || [] })
  let [b, a] = [before.pageNumberings || [], after.pageNumberings || []]
  let samePage = (x, y) => +x.pagePos === +y.pagePos
  let [bNumbers, aNumbers] = [numbers(before), numbers(after)]

  return [
    ...a.filter((restart) => !b.some((old) => samePage(old, restart) && +old.startWith === +restart.startWith)).map((restart) => [`Page numbering restarts at p. ${bNumbers[+restart.pagePos]} with ${restart.startWith}`]),
    ...b.filter((restart) => !a.some((now) => samePage(now, restart))).map((restart) => [`Page numbering no longer restarts at p. ${aNumbers[+restart.pagePos]}`]),
  ]
}

const FLATPLAN_WORDS = [
  [["archivedAt"], (before, after) => [[after.archivedAt ? "Archived the flatplan" : "Unarchived the flatplan"]]],
  [["issue"], (before, after) => [changed("Renamed the flatplan ", before.issue, after.issue)]],
  [["subheading"], (before, after) => [changed("Subheading: ", before.subheading, after.subheading)]],
  [["singlePages"], (before, after) => [[after.singlePages ? "Switched to single pages" : "Switched to spreads"]]],
  [["pageOrientation"], (before, after) => [[`Page orientation: ${before.pageOrientation} → ${after.pageOrientation}`]]],
  [["displayCoverPageNumbering"], (before, after) => [[`Turned cover numbering ${after.displayCoverPageNumbering ? "on" : "off"}`]]],
  [["pageNumberings"], (before, after, ctx) => restartSentences(before, after, ctx)],
  [["notes"], (before, after) => notesSentence(before.notes, after.notes, "flatplan notes")],
]

const COPIED = { categories: "categories and tags", layout: "the layout", all: "everything" }

const flatplanRow = (activity, ctx) => {
  let labels = activity.labels || {}

  return activity.changes.flatMap((change) => {
    let [before, after] = [change.before || {}, change.after || {}]
    if (change.op === "create") {
      let source = after.clonedFrom && (labels[after.clonedFrom] ? quote(labels[after.clonedFrom].name) : "a deleted flatplan")
      return [["Created the flatplan", ...(source ? [`, copying ${COPIED[after.clone]} from `, source] : [])]]
    }
    return fieldSentences(FLATPLAN_WORDS, before, after, ctx)
  })
}

// a tag's colour can be blank
const swatch = (color) => (color ? { kind: "swatch", color } : "none")

// a tag's colour, round as the flatplan dots it under a page
const dot = (color) => (color ? { kind: "dot", color } : "none")

// Categories and tags: 'Renamed category "Advert" → "Ads"', "Recoloured category Ads ■ → ■".
// Edits carry the name even when it didn't change.
const nameAndColour = (noun, mark = swatch) => {
  let name = (values) => `${noun} ${htmlDecode(values.name || "")}`
  return {
    name: (values) => [name(values)],
    words: [
      [["name"], (before, after) => [before.name !== after.name && changed(`Renamed ${noun} `, before.name, after.name)]],
      [["color"], (before, after) => [before.color !== after.color && [`Recoloured ${name(after)} `, mark(before.color), " → ", mark(after.color)]]],
    ],
  }
}

const section = (values) => `${(values.type || "Content").toLowerCase()} section ${values.count} × ${values.size}`

const shown = (value) => (Array.isArray(value) ? value.join(", ") : value) || "none"

// a stitch-in's page is an index into the page numbers
const afterPage = (index, ctx) => `after p. ${ctx.pageNumbers[index]}`

const stitchInCover = (field, side) => [[field], (before, after, ctx) => [before[field] !== after[field] && changed(`Stitch-in ${afterPage(after.pageNumber, ctx)}: ${side} cover `, before[field], after[field])]]

const contentField = (field, label) => [[field], (before, after) => [["Content ", quote(after.text), `: ${label} ${shown(before[field])} → ${shown(after[field])}`]]]

// Everything but pages and the flatplan, each with how it's named and how its edits read. Edits carry
// the fields that name the item (data's NAMED_BY) even when they didn't change.
const ITEMS = {
  categories: nameAndColour("category"),
  tags: nameAndColour("tag", dot),
  sections: {
    name: (values) => [section(values)],
    words: [
      [
        ["type", "count", "size"],
        (before, after) => [before.type === after.type ? [`${after.type || "Content"} section: ${before.count} × ${before.size} → ${after.count} × ${after.size}`] : [`${capitalize(section(before))} → ${section(after)}`]],
      ],
    ],
  },
  stitchIns: {
    name: (values, ctx) => [`stitch-in ${afterPage(values.pageNumber, ctx)}`],
    words: [
      [["pageNumber"], (before, after, ctx) => [before.pageNumber !== after.pageNumber && [`Moved the stitch-in ${afterPage(before.pageNumber, ctx)} → p. ${ctx.pageNumbers[after.pageNumber]}`]]],
      stitchInCover("frontContent", "front"),
      stitchInCover("backContent", "back"),
    ],
  },
  contents: {
    name: (values) => ["content ", quote(values.text)],
    words: [
      [["text"], (before, after) => [before.text !== after.text && changed("Content text: ", before.text, after.text)]],
      contentField("identifier", "identifier"),
      contentField("pageNos", "page numbers"),
      contentField("size", "size"),
      contentField("shape", "shape"),
    ],
  },
}

const itemRow = (item) => (activity, ctx) =>
  activity.changes.flatMap(({ op, before, after }) => {
    if (op === "update") return fieldSentences(item.words, before || {}, after || {}, ctx)
    return DONE[op] ? [[`${capitalize(DONE[op])} `, ...item.name((op === "delete" ? before : after) || {}, ctx)]] : []
  })

const itemRows = (item) => {
  let row = itemRow(item)
  return { create: row, update: row, destroy: row }
}

// A content batch (an import, an undo or an API sync) holding several changes is summed up first
const contentBatchRow = (activity, ctx) => {
  let sentences = itemRow(ITEMS.contents)(activity, ctx)
  if (activity.changes.length < 2) return sentences

  let counts = Object.keys(DONE)
    .map((op) => [op, activity.changes.filter((change) => change.op === op).length])
    .filter(([, n]) => n)
    .map(([op, n]) => `${DONE[op]} ${n} ${plural(n, "content")}`)
  return [[capitalize(counts.join(", "))], ...sentences]
}

// Rows without wording here (written by a newer data service) describe as [] and aren't shown.
const describers = {
  pages: { update: pageRow, reorder: pageRow, batchCreate: pageRow, batchUpdate: pageRow, batchStretch: pageRow, batchDestroy: pageRow, destroy: pageRow, createTags: itemRow(ITEMS.tags) },
  flatplans: { create: flatplanRow, update: flatplanRow, archive: flatplanRow },
  categories: { ...itemRows(ITEMS.categories), reorder: () => [["Reordered categories"]] },
  tags: { ...itemRows(ITEMS.tags), reorder: () => [["Reordered tags"]] },
  sections: itemRows(ITEMS.sections),
  stitchIns: itemRows(ITEMS.stitchIns),
  contents: { update: itemRow(ITEMS.contents), batchCreate: contentBatchRow, batchDestroy: contentBatchRow },
}

// ctx: useActivity().ctx: { pageIds: Set of the current page ids, order: the flatplan's page ids (spaces
// included), numbering: usePageNumbers().numbering, pageNumbers: today's page numbers }
export const describe = (activity, ctx) => {
  let describer = describers[activity.controller]?.[activity.type]
  return describer ? describer(activity, { ...ctx, pageNumbers: pageNumbersFor(activity, ctx) }) : []
}

// The page a list is filtered to, for its chip: "p. 12"
export const pageName = (pageId, ctx) => {
  let ref = pageRef(pageId, ctx)
  return ref.text ?? ref
}

const initialsOf = (name) =>
  name
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

const named = (name) => ({ name, initials: initialsOf(name) })

// A person shows the initials they chose, as on their comments (rows written before tokens carried them
// show the name's)
export const who = (actor) => {
  let person = actor?.name || actor?.email
  if (actor && "apiKey" in actor) return named(`API key ${actor.apiKey || `…${actor.last4}`}`)
  if (actor?.impersonatedBy) return named(`Blinkplan support (as ${person})`)
  if (person) return { name: person, initials: actor.initials || initialsOf(person) }
  return named("Blinkplan")
}
